import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, Send, Upload, Calendar, MapPin, User, Clock, AlertTriangle } from 'lucide-react';

const STATUS_LABEL = {
  open: 'Open',
  in_progress: 'In progress',
  blocked: 'Blocked',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const PRIORITY_META = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-slate-100 text-slate-800',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800',
};

const formatDate = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch (_) {
    return v;
  }
};

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = getUser();

  const [ticket, setTicket] = useState(null);
  const [activity, setActivity] = useState([]);
  const [users, setUsers] = useState([]);
  const [comment, setComment] = useState('');
  const [pendingCommentImg, setPendingCommentImg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const fileRef = useRef(null);
  const commentFileRef = useRef(null);

  const isAdmin = user?.role === 'admin';
  const isCreator = ticket && ticket.assigned_by === user?.id;
  const isAssignee = ticket && (ticket.assigned_to || []).includes(user?.id);
  const canEditStructural = isAdmin || (user?.role === 'staff' && isCreator);
  const canChangeStatus = isAdmin || isCreator || isAssignee;

  const load = async () => {
    try {
      setLoading(true);
      const [{ data: t }, { data: a }] = await Promise.all([
        api.get(`/api/tickets/${id}`),
        api.get(`/api/tickets/${id}/activity`),
      ]);
      setTicket(t);
      setActivity(a || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load ticket');
      if (e.response?.status === 403 || e.response?.status === 404) {
        navigate('/tickets');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const { data } = await api.get('/api/tickets/assignable-users');
      const arr = Array.isArray(data) ? data : [];
      setUsers(arr);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    if (user?.role === 'admin' || user?.role === 'staff' || user?.role === 'field_team') {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const nameById = useMemo(() => {
    const m = {};
    users.forEach((u) => (m[u.id] = u.name || u.email || u.id));
    return m;
  }, [users]);

  const changeStatus = async (newStatus) => {
    if (!canChangeStatus || newStatus === ticket.status) return;
    setSavingStatus(true);
    try {
      await api.patch(`/api/tickets/${id}`, { status: newStatus });
      toast.success('Status updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    } finally {
      setSavingStatus(false);
    }
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/api/tickets/${id}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Image attached');
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    }
  };

  const submitComment = async () => {
    if (!comment.trim() && !pendingCommentImg) {
      toast.error('Enter a comment or attach a photo');
      return;
    }
    try {
      let attachment_url = null;
      if (pendingCommentImg) {
        const fd = new FormData();
        fd.append('file', pendingCommentImg);
        const { data } = await api.post(`/api/tickets/${id}/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        attachment_url = data.image_url;
      }
      await api.post(`/api/tickets/${id}/comments`, {
        message: comment.trim(),
        attachment_url,
      });
      setComment('');
      setPendingCommentImg(null);
      if (commentFileRef.current) commentFileRef.current.value = '';
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add comment');
    }
  };

  if (loading || !ticket) {
    return (
      <Layout title="Ticket">
        <div className="max-w-5xl mx-auto p-6 text-gray-500">Loading…</div>
      </Layout>
    );
  }

  return (
    <Layout title={ticket.ticket_no}>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="ticket-detail-page">
        <Button
          variant="outline"
          onClick={() => navigate('/tickets')}
          data-testid="back-btn"
          className="w-fit"
        >
          <ArrowLeft size={14} className="mr-1" /> Back to list
        </Button>

        {/* Header card */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={PRIORITY_META[ticket.priority] + ' border-0'}>
                    {ticket.priority}
                  </Badge>
                  <span className="font-mono text-xs text-gray-500">{ticket.ticket_no}</span>
                  <span className="text-xs text-gray-500">· {ticket.category || 'General'}</span>
                  {ticket.is_overdue && (
                    <Badge className="bg-red-100 text-red-700 border-0" data-testid="overdue-tag">
                      <AlertTriangle size={12} className="mr-1" /> OVERDUE
                    </Badge>
                  )}
                </div>
                <h2 className="text-2xl font-semibold mt-1" data-testid="ticket-title">{ticket.title}</h2>
              </div>
              {canChangeStatus && (
                <div>
                  <Select
                    value={ticket.status}
                    onValueChange={changeStatus}
                    disabled={savingStatus}
                  >
                    <SelectTrigger className="w-44" data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABEL).map(([k, l]) => (
                        <SelectItem key={k} value={k}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {ticket.description && (
              <div className="text-sm text-gray-800 whitespace-pre-wrap" data-testid="ticket-description">
                {ticket.description}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Meta icon={User} label="Assigned by" value={ticket.assigned_by_name || nameById[ticket.assigned_by] || ticket.assigned_by || '—'} />
              <Meta
                icon={User}
                label="Assignees"
                value={
                  (ticket.assigned_to_names && ticket.assigned_to_names.length > 0
                    ? ticket.assigned_to_names
                    : (ticket.assigned_to || []).map((uid) => nameById[uid] || uid)
                  ).join(', ') || '—'
                }
                testId="assignees-value"
              />
              <Meta icon={Calendar} label="Due" value={formatDate(ticket.due_date)} />
              <Meta icon={MapPin} label="Location" value={ticket.location || '—'} />
              <Meta icon={Clock} label="Created" value={formatDate(ticket.created_at)} />
              {ticket.completed_at && (
                <Meta icon={Clock} label="Completed" value={formatDate(ticket.completed_at)} />
              )}
            </div>

            {/* Attachments */}
            {(ticket.attachments || []).length > 0 && (
              <div>
                <div className="text-xs uppercase text-gray-500 mb-2">Attachments</div>
                <div className="flex flex-wrap gap-2">
                  {ticket.attachments.map((url, i) => (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                      data-testid={`attachment-${i}`}
                    >
                      <img
                        src={url}
                        alt="attachment"
                        className="w-24 h-24 object-cover rounded-md border hover:opacity-90"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-emerald-700 hover:underline">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={uploadAttachment}
                  data-testid="attachment-upload-input"
                />
                <Upload size={14} /> Attach image
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Activity + comment */}
        <Card>
          <CardContent className="p-6">
            <div className="text-lg font-semibold mb-4">Activity</div>
            {activity.length === 0 ? (
              <div className="text-sm text-gray-500">No activity yet.</div>
            ) : (
              <div className="space-y-4" data-testid="activity-feed">
                {activity.map((a) => (
                  <div key={a.id} className="border-l-2 border-gray-200 pl-3">
                    <div className="text-xs text-gray-600">
                      <span className="font-medium text-gray-900">{a.user_name}</span>
                      <span className="text-gray-400"> · {a.user_role}</span>
                      <span className="text-gray-400"> · {formatDate(a.created_at)}</span>
                      <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-gray-100 uppercase tracking-wide">
                        {a.type}
                      </span>
                    </div>
                    <div className="text-sm mt-1 whitespace-pre-wrap">{a.message}</div>
                    {a.attachment_url && (
                      <a
                        href={a.attachment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-2"
                      >
                        <img
                          src={a.attachment_url}
                          alt="comment attachment"
                          className="w-32 h-32 object-cover rounded-md border"
                        />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 border-t pt-4">
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                placeholder="Add a comment or note…"
                data-testid="comment-input"
              />
              {pendingCommentImg && (
                <div className="mt-2 text-xs text-gray-600 flex items-center gap-2">
                  <img
                    src={URL.createObjectURL(pendingCommentImg)}
                    alt=""
                    className="w-12 h-12 object-cover rounded"
                  />
                  <span>{pendingCommentImg.name}</span>
                  <button
                    type="button"
                    onClick={() => setPendingCommentImg(null)}
                    className="text-red-600"
                  >
                    remove
                  </button>
                </div>
              )}
              <div className="mt-2 flex items-center gap-2">
                <label className="inline-flex items-center gap-1 text-sm cursor-pointer text-gray-700">
                  <input
                    ref={commentFileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setPendingCommentImg(e.target.files?.[0] || null)}
                    data-testid="comment-attach-input"
                  />
                  <Upload size={14} /> Attach photo
                </label>
                <div className="flex-1" />
                <Button onClick={submitComment} data-testid="comment-submit">
                  <Send size={14} className="mr-1" /> Post
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

function Meta({ icon: Icon, label, value, testId }) {
  return (
    <div>
      <div className="text-xs uppercase text-gray-500 flex items-center gap-1">
        <Icon size={12} /> {label}
      </div>
      <div className="text-sm text-gray-800 mt-0.5" data-testid={testId}>{value}</div>
    </div>
  );
}
